/*
 * Состояние входа.
 *
 * Единственное место, откуда интерфейс узнаёт, кто вошёл и подтверждена ли
 * почта. Экраны обращаются сюда, а не к слою запросов напрямую, — иначе
 * восстановление сессии пришлось бы повторять в каждом из них.
 */

import * as api from '$api/auth';
import type { RegistrationDraft, RegistrationUpdate } from '@andresot/protocol';
import { ApiError, NetworkError, restoreSession, setAccessToken } from '$api/client';
import { app } from './app.svelte';
import { maintenance } from './maintenance.svelte';
import { stories } from './stories.svelte';
import { prefs } from './prefs.svelte';
import { push } from './push.svelte';
import { mail } from './mail.svelte';
import { finance } from './finance.svelte';
import { trading } from './trading.svelte';
import { clearSnapshot, readSnapshot } from '$utils/snapshot';
import { clearDrafts } from '$utils/drafts';

export type SessionPhase =
	/** Ещё не знаем: идёт попытка восстановить сессию по печенью. */
	| 'loading'
	/** Не вошёл. */
	| 'anonymous'
	/** Вошёл, но почта не подтверждена — ящик ещё не выдан. */
	| 'unverified'
	/**
	 * Связи нет, и кто вошёл — неизвестно.
	 *
	 * Отдельно от `anonymous`: там человек точно не вошёл, здесь мы просто не
	 * смогли спросить. Разница видна на экране — объяснение вместо чужого
	 * экрана входа.
	 */
	| 'offline'
	/** Вошёл полностью. */
	| 'ready';

/*
 * Выход, не дошедший до сервера.
 *
 * Печенье с токеном обновления страница стереть не может — это делает только
 * сервер. Если в минуту выхода связи не было, оно остаётся, и следующий запуск
 * возвращал человека в аккаунт без пароля: на общем компьютере — прямо тому,
 * кто сядет следом. Отметка заставляет довести выход до конца при первой связи.
 */
const LOGOUT_PENDING_KEY = 'andresot:logout-pending';

function logoutPending(): boolean {
	try {
		return localStorage.getItem(LOGOUT_PENDING_KEY) === '1';
	} catch {
		return false;
	}
}

function markLogoutPending(pending: boolean): void {
	try {
		if (pending) localStorage.setItem(LOGOUT_PENDING_KEY, '1');
		else localStorage.removeItem(LOGOUT_PENDING_KEY);
	} catch {
		// Хранилище закрыто — отметку держать негде; выход на этом устройстве всё равно выполнен.
	}
}

/** Учётная запись в том виде, в каком её знает мессенджер. */
function toMessengerUser(user: api.AccountUser) {
	return {
		id: user.id,
		login: user.login,
		name: user.name,
		lastName: user.lastName,
		shortName: user.name.split(/\s+/)[0] ?? user.name,
		bio: user.bio,
		avatarUrl: user.avatarUrl,
		avatarColor: user.avatarColor,
		isVerified: user.isVerified,
		isPremium: user.isPremium,
		emojiStatus: user.emojiStatus,
		profileAccent: user.profileAccent,
		serviceRole: user.serviceRole,
		presence: 'online' as const
	};
}

class SessionState {
	phase = $state<SessionPhase>('loading');
	user = $state<api.AccountUser | null>(null);

	/** Идёт ли ожидание связи после запуска без сети. */
	private reconnecting = false;
	private lifecycle = 0;
	private stopWaiting: (() => void) | null = null;
	private logoutRun: { promise: Promise<void> } | null = null;
	private logoutGeneration = 0;
	/** Ждём ли возвращения сети, чтобы довести выход до сервера. */
	private logoutRetryArmed = false;

	/** Вошёл ли человек вообще — независимо от подтверждения почты. */
	isSignedIn = $derived(this.phase === 'unverified' || this.phase === 'ready');

	/**
	 * Восстановить сессию при запуске. Токен доступа живёт 15 минут и
	 * при перезагрузке страницы теряется, но печенье с токеном обновления
	 * остаётся — по нему и восстанавливаемся, без повторного ввода пароля.
	 */
	async restore(): Promise<void> {
		this.stopWaiting?.();
		const lifecycle = ++this.lifecycle;
		this.phase = 'loading';

		/*
		 * Человек вышел, а сервер об этом не узнал. Сессию не восстанавливаем ни при
		 * каком исходе: сначала доводим выход, и в любом случае показываем вход.
		 */
		if (logoutPending()) {
			await this.finishLogout();
			if (lifecycle === this.lifecycle) this.phase = 'anonymous';
			return;
		}

		const restored = await restoreSession();
		if (lifecycle !== this.lifecycle) return;

		// Сети нет — сессию не трогаем вовсе: она, скорее всего, жива.
		if (restored === 'offline') {
			await this.openFromCache(lifecycle);
			return;
		}

		if (restored === 'expired') {
			const cached = await readSnapshot();
			if (lifecycle !== this.lifecycle) return;
			const userId = cached?.userId ?? app.me.id;
			this.forget();
			// Сессии больше нет — сохранённой переписке на устройстве тоже не
			// место: следующим здесь может оказаться другой человек.
			if (userId) void clearDrafts(userId);
			return;
		}

		try {
			const user = await api.fetchMe();
			if (lifecycle === this.lifecycle) this.apply(user);
		} catch (error) {
			if (lifecycle !== this.lifecycle) return;
			if (error instanceof NetworkError) {
				this.phase = 'offline';
				return;
			}
			// Сбой сервера (5xx, 429) — не отказ в доступе: сессию и сохранённое на
			// устройстве не трогаем, открываемся по сохранённому и пробуем снова.
			if (error instanceof ApiError && error.status !== 401 && error.status !== 403) {
				await this.openFromCache(lifecycle);
				return;
			}
			this.forget();
		}
	}

	async register(input: api.RegisterInput): Promise<void> {
		await this.settleLogout();
		const lifecycle = ++this.lifecycle;
		const user = await api.register(input);
		if (lifecycle === this.lifecycle) this.apply(user);
	}

	/** Недоведённый выход — раньше нового входа: иначе он догнал бы уже новую сессию. */
	private async settleLogout(): Promise<void> {
		if (logoutPending()) await this.finishLogout();
	}

	async registration(): Promise<RegistrationDraft> {
		return api.fetchRegistration();
	}

	async updateRegistration(input: RegistrationUpdate): Promise<api.RegistrationUpdateResult> {
		const lifecycle = this.lifecycle;
		const result = await api.updateRegistration(input);
		if (lifecycle === this.lifecycle) this.apply(result.user);
		return result;
	}

	async login(login: string, password: string): Promise<void> {
		await this.settleLogout();
		const lifecycle = ++this.lifecycle;
		const user = await api.login(login, password);
		if (lifecycle === this.lifecycle) this.apply(user);
	}
	async completeMfa(mfa: api.MfaRequired, code: string, beforeApply: () => void): Promise<void> {
		const lifecycle = ++this.lifecycle;
		const user = await api.finishMfaLogin(mfa, code);
		if (lifecycle === this.lifecycle) {
			beforeApply();
			this.apply(user);
		}
	}

	async verify(code: string): Promise<void> {
		const lifecycle = ++this.lifecycle;
		const user = await api.verifyEmail(code);
		if (lifecycle === this.lifecycle) this.apply(user);
	}

	async changeVerificationEmail(email: string): Promise<string> {
		const changed = await api.changeVerificationEmail({ email });
		return changed.email;
	}

	/**
	 * Вход по QR-коду. Токен доступа уже сохранён слоем запросов при опросе,
	 * здесь остаётся применить пользователя — тем же путём, что обычный вход.
	 */
	completeQrLogin(user: api.AccountUser): void {
		this.apply(user);
	}

	async resendCode(): Promise<void> {
		await api.resendCode();
	}

	/**
	 * Довести до сервера выход, который до него не дошёл.
	 *
	 * Не дошёл и сейчас — отметка остаётся, и попытка повторится, когда вернётся
	 * сеть или приложение запустят снова.
	 */
	private finishLogout(): Promise<void> {
		if (this.logoutRun) return this.logoutRun.promise;
		const run = { promise: null as unknown as Promise<void> };
		run.promise = this.performLogout(this.logoutGeneration).finally(() => {
			if (this.logoutRun === run) this.logoutRun = null;
		});
		this.logoutRun = run;
		return run.promise;
	}

	private async performLogout(generation: number): Promise<void> {
		try {
			await api.logout();
			if (generation !== this.logoutGeneration) return;
			markLogoutPending(false);
		} catch {
			if (generation !== this.logoutGeneration) return;
			if (typeof window === 'undefined' || this.logoutRetryArmed) return;
			this.logoutRetryArmed = true;
			window.addEventListener(
				'online',
				() => {
					if (generation !== this.logoutGeneration) return;
					this.logoutRetryArmed = false;
					if (logoutPending()) void this.finishLogout();
				},
				{ once: true }
			);
		}
	}

	async logout(): Promise<void> {
		markLogoutPending(true);
		// Запрос получает текущий токен до синхронного локального выхода.
		const pending = this.finishLogout();
		this.forget();
		await pending;
	}

	/**
	 * Открыть приложение по сохранённому на устройстве, пока нет связи.
	 *
	 * До этого запуск без сети упирался в объяснение обрыва: сервер не
	 * отвечает, кто вошёл, — и показывать было нечего. Но последний вид
	 * приложения лежит на устройстве, и он не хуже того, что человек видел
	 * минуту назад.
	 *
	 * Соединение при этом не поднимается: короткого токена нет, и попытка
	 * закончилась бы отказом в доступе, то есть выходом из аккаунта. Поэтому
	 * ждём возвращения сети и входим обычным путём.
	 */
	private async openFromCache(lifecycle: number): Promise<void> {
		const saved = await readSnapshot();
		if (lifecycle !== this.lifecycle) return;
		const account = saved?.account;

		if (!account) {
			this.phase = 'offline';
			return;
		}

		if (this.user?.id !== account.id) {
			finance.reset();
			trading.reset();
		}
		this.user = account;
		prefs.beginNotifications(account.id);
		// Иначе следующая запись на устройство ушла бы без учётной записи, и
		// второй раз без сети приложение уже не открылось бы.
		app.account = account;
		this.phase = account.emailVerified ? 'ready' : 'unverified';
		if (this.phase === 'ready') await app.startFromCache(toMessengerUser(account));

		if (lifecycle === this.lifecycle) this.waitForNetwork(lifecycle);
	}

	/**
	 * Дождаться связи и войти по-настоящему.
	 *
	 * Возвращение сети браузер сообщает сам, но не всегда: `online` означает
	 * лишь появление сети у устройства, а не доступность сервера — при
	 * включённом VPN одно с другим не совпадает. Поэтому ещё и по времени.
	 */
	private waitForNetwork(lifecycle: number): void {
		if (this.reconnecting) return;
		this.reconnecting = true;

		const attempt = async () => {
			if (lifecycle !== this.lifecycle || (this.phase !== 'ready' && this.phase !== 'unverified'))
				return stop();

			const restored = await restoreSession();
			if (lifecycle !== this.lifecycle) return;
			if (restored === 'offline') return;

			stop();
			if (restored === 'expired') {
				this.forget();
				return;
			}

			try {
				const user = await api.fetchMe();
				if (lifecycle === this.lifecycle) this.apply(user);
			} catch {
				// Ответ не разобрали — следующая попытка входа будет обычной,
				// из интерфейса. Сохранённое на экране от этого не пострадает.
			}
		};

		const timer = setInterval(() => void attempt(), 15_000);
		const onOnline = () => void attempt();
		const stop = () => {
			clearInterval(timer);
			window.removeEventListener('online', onOnline);
			this.reconnecting = false;
			if (this.stopWaiting === stop) this.stopWaiting = null;
		};

		this.stopWaiting = stop;
		window.addEventListener('online', onOnline);
	}

	/** Применить подтверждённое сервером изменение профиля без перезапуска приложения. */
	updateProfile(user: api.AccountUser): void {
		this.user = user;
		app.updateProfile({
			name: user.name,
			lastName: user.lastName,
			bio: user.bio,
			avatarUrl: user.avatarUrl,
			avatarColor: user.avatarColor,
			emojiStatus: user.emojiStatus,
			profileAccent: user.profileAccent,
			serviceRole: user.serviceRole
		});
	}

	/** Синхронно применить смену логина во всех трёх копиях своей учётной записи. */
	applyLoginChange(login: string, mailbox: string | null): void {
		const patch = { login, mailbox: mailbox ?? undefined };
		if (this.user) this.user = { ...this.user, ...patch };
		if (app.account) app.account = { ...app.account, ...patch };
		app.me.login = login;
	}

	private apply(user: api.AccountUser): void {
		this.stopWaiting?.();
		this.lifecycle += 1;
		this.logoutGeneration += 1;
		this.logoutRun = null;
		this.logoutRetryArmed = false;
		if (this.user?.id !== user.id) {
			finance.reset();
			trading.reset();
			mail.stop();
			stories.stop();
		}
		prefs.beginNotifications(user.id);
		// Новый вход отменяет недоведённый выход: печенье уже принадлежит этой сессии,
		// и «довести выход» значило бы выкинуть только что вошедшего.
		markLogoutPending(false);
		this.user = user;
		this.phase = user.emailVerified ? 'ready' : 'unverified';
		/*
		 * Кому не показывать экран технических работ. Ставится прямо здесь, а
		 * не отдельным наблюдателем: мессенджер запускается строкой ниже, и к
		 * этому моменту роль уже должна быть известна — иначе администратор
		 * увидел бы экран, хотя сервер его пускает.
		 */
		maintenance.staff = user.serviceRole !== 'user';
		// Нужна для запуска без сети — мессенджер сохранит её вместе с чатами.
		app.account = user;

		// Данные тянем только после подтверждения почты: до него человек
		// внутрь не попадает, и загружать было бы нечего.
		if (this.phase === 'ready') void this.startApp(user);
	}

	/**
	 * Запустить мессенджер: загрузить чатов и установить соединение.
	 *
	 * Живёт здесь, а не в самом мессенджере, потому что момент известен
	 * только тут — ровно тогда, когда стало ясно, кто вошёл.
	 */
	private async startApp(user: api.AccountUser): Promise<void> {
		const lifecycle = this.lifecycle;
		/*
		 * Во время технических работ мессенджер не запускаем: сервер всё равно
		 * откажет, а каждый отказ добавил бы к экрану работ ещё и сообщение об
		 * ошибке. Персонала это не касается — они в это время и работают.
		 */
		if (maintenance.blocking) return;

		await app.start(toMessengerUser(user), () => {
			if (lifecycle === this.lifecycle) this.forget();
		});
		if (lifecycle !== this.lifecycle || this.user?.id !== user.id) return;
		/*
		 * Лента историй грузится вместе с остальным, а не перед ним: раньше
		 * вход ждал её отдельной строкой, хотя чатам она не нужна.
		 */
		await Promise.allSettled([
			stories.start(user.id),
			prefs.loadNotifications(),
			push.init(),
			mail.start(user.mailbox)
		]);
	}

	/** Сбросить состояние, не обращаясь к серверу, — при отзыве сессии. */
	forget(): void {
		this.lifecycle += 1;
		this.stopWaiting?.();
		finance.reset();
		trading.reset();
		prefs.beginNotifications(null);
		const userId = this.user?.id ?? app.me.id;
		setAccessToken(null);
		stories.stop();
		mail.stop();
		app.stop();
		push.reset();
		void clearSnapshot();
		if (userId) void clearDrafts(userId);
		app.account = null;
		this.user = null;
		this.phase = 'anonymous';
	}
}

export const session = new SessionState();
